// AI Recording Studio Pro — Dedicated Layout
// Full-screen studio layout with specialized sidebar navigation
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Clapperboard,
  LayoutDashboard,
  MonitorPlay,
  FolderKanban,
  Settings,
  LogOut,
  User,
  Menu,
  ArrowLeft,
  Server,
  Brain,
  Mic,
  Film,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { getInitials } from "@/lib/utils";

const STUDIO_NAV = [
  { id: "dashboard", href: "/studio", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { id: "record", href: "/studio/record", label: "Recording Studio", icon: MonitorPlay },
  { id: "projects", href: "/studio/projects", label: "Projets", icon: FolderKanban },
  { id: "divider-1", divider: true, label: "Outils" },
  { id: "ssh", href: "/studio/ssh", label: "SSH Manager", icon: Server },
  { id: "divider-2", divider: true, label: "IA & Édition" },
  { id: "ai", href: "/studio/ai", label: "AI Assistant", icon: Brain },
  { id: "voice", href: "/studio/voice", label: "Voice AI", icon: Mic },
  { id: "editor", href: "/studio/editor", label: "Video Editor", icon: Film },
  { id: "export", href: "/studio/export", label: "Export", icon: Download },
  { id: "divider-3", divider: true, label: "Système" },
  { id: "settings", href: "/studio/settings", label: "Paramètres", icon: Settings },
];

export const RecordingStudioLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.href;
    return location.pathname.startsWith(item.href);
  };

  const NavContent = ({ isMobile = false }) => (
    <div className="flex h-full flex-col">
      {/* Studio Header */}
      <div className="border-b border-border/50">
        <div className={`flex items-center gap-3 ${collapsed && !isMobile ? "justify-center px-3" : "px-5"} h-14`}>
          <div
            className="flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #ef4444 0%, #f97316 50%, #eab308 100%)",
              boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
            }}
          >
            <Clapperboard className="h-5 w-5 text-white" />
          </div>
          {(!collapsed || isMobile) && (
            <div className="min-w-0">
              <span className="text-sm font-bold tracking-tight block truncate">
                Recording Studio
              </span>
              <span className="text-[10px] text-muted-foreground tracking-wider uppercase block">
                AI Pro
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Back to Platform */}
      <div className={`${collapsed && !isMobile ? "px-2" : "px-3"} pt-3 pb-1`}>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className={`w-full gap-2 text-muted-foreground hover:text-foreground ${collapsed && !isMobile ? "justify-center px-0" : "justify-start"}`}
                data-testid="back-to-platform"
              >
                <ArrowLeft className="h-4 w-4 flex-shrink-0" />
                {(!collapsed || isMobile) && <span className="text-xs">Retour plateforme</span>}
              </Button>
            </TooltipTrigger>
            {collapsed && !isMobile && (
              <TooltipContent side="right">Retour plateforme</TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 overflow-y-auto ${collapsed && !isMobile ? "px-2" : "px-3"} py-2 space-y-0.5`}>
        <TooltipProvider delayDuration={0}>
          {STUDIO_NAV.map((item) => {
            if (item.divider) {
              if (collapsed && !isMobile) {
                return (
                  <div key={item.id} className="my-2">
                    <div className="h-px bg-border/50 mx-1" />
                  </div>
                );
              }
              return (
                <div key={item.id} className="pt-4 pb-1 px-3">
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/60">
                    {item.label}
                  </span>
                </div>
              );
            }

            const active = isActive(item);
            const Icon = item.icon;

            const linkContent = (
              <Link
                to={item.href}
                className={`
                  flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all duration-150
                  ${collapsed && !isMobile ? "justify-center px-2" : ""}
                  ${active
                    ? "bg-primary/10 text-primary font-medium shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }
                `}
                data-testid={`studio-nav-${item.id}`}
              >
                <Icon className={`h-4 w-4 flex-shrink-0 ${active ? "text-primary" : ""}`} />
                {(!collapsed || isMobile) && (
                  <span className="truncate flex-1">{item.label}</span>
                )}
              </Link>
            );

            if (collapsed && !isMobile) {
              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                  <TooltipContent side="right">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return <div key={item.id}>{linkContent}</div>;
          })}
        </TooltipProvider>
      </nav>

      {/* Collapse toggle */}
      {!isMobile && (
        <div className="border-t border-border/50 p-2 flex justify-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      )}

      {/* User section */}
      <div className={`border-t border-border/50 ${collapsed && !isMobile ? "p-2" : "p-3"}`}>
        <div className={`flex items-center gap-2.5 ${collapsed && !isMobile ? "justify-center" : "px-2"} py-2 rounded-xl`}>
          <Avatar className="h-8 w-8 ring-2 ring-primary/20 flex-shrink-0">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-gradient-to-br from-red-500 to-orange-500 text-white text-[10px] font-bold">
              {getInitials(user?.first_name, user?.last_name)}
            </AvatarFallback>
          </Avatar>
          {(!collapsed || isMobile) && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-[10px] text-muted-foreground capitalize">{user?.role}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col border-r border-border/50 bg-card/50 transition-all duration-300 ${
          collapsed ? "w-16" : "w-60"
        }`}
      >
        <NavContent />
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="flex h-12 items-center justify-between px-4 border-b border-border/50 bg-background/80 backdrop-blur-md z-40 flex-shrink-0">
          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden h-8 w-8">
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-60 p-0">
              <NavContent isMobile />
            </SheetContent>
          </Sheet>

          {/* Breadcrumb / Page title */}
          <div className="flex items-center gap-2">
            <Clapperboard className="h-4 w-4 text-primary md:hidden" />
            <span className="text-xs font-medium text-muted-foreground hidden md:block">
              AI Recording Studio Pro
            </span>
          </div>

          <div className="flex-1" />

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 rounded-xl hover:bg-primary/5 h-8" data-testid="studio-user-menu">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-gradient-to-br from-red-500 to-orange-500 text-white text-[9px] font-bold">
                      {getInitials(user?.first_name, user?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline-block text-xs font-medium">{user?.first_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-xl shadow-xl border-border/50">
                <DropdownMenuLabel className="text-xs">Mon compte</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} className="rounded-lg cursor-pointer text-xs">
                  <User className="mr-2 h-3.5 w-3.5" />
                  Profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard")} className="rounded-lg cursor-pointer text-xs">
                  <ArrowLeft className="mr-2 h-3.5 w-3.5" />
                  Retour plateforme
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="rounded-lg cursor-pointer text-rose-500 focus:text-rose-500 text-xs">
                  <LogOut className="mr-2 h-3.5 w-3.5" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
